import React, { useEffect, useState, useRef, useMemo, useCallback } from "react"
import styles from "./Feed.module.scss"
import { connect, useSelector } from "react-redux"
import {
  openFeedTextModal,
  closeMainModal,
  fetchPosteosWithComments,
  fetchOwnFeedContent,
  resetRecentPost,
  refetchPortfolios,
  setFeedView,
  shuffleFeedOrder,
} from "../../../store/actions"
import { useActionGuard } from "../../../hooks/useActionGuard.ts"
import FeedCard from "./FeedCard.tsx"
import PortfolioCard from "./PortfolioCard/PortfolioCard.tsx"
import { SlidersHorizontal, FileText, ChevronDown } from "lucide-react"
import FeedCardSkeleton from "./FeedCardSkeleton.tsx"
import useIsPhone from "../../../hooks/useIsPhone"
import type {
  FeedFilterOption,
  FeedItem,
  FeedPost,
  FeedPortfolio,
  FeedProps,
} from "../../../types/feed.ts"
import {
  selectFeedView,
  selectFeedItemsFromOrder,
  computeItemOrder,
} from "../../../store/feed/selectors.ts"

const Feed = ({
  userData,
  openFeedTextModal,
  closeMainModal,
  fetchPosteosWithComments,
  fetchOwnFeedContent,
  ownPosteos,
  resetRecentPost,
  posteos,
  recentPost,
  componentLoading,
  refetchPortfolios,
  allPortfolios,
  setFeedView,
  shuffleFeedOrder: dispatchShuffleFeedOrder,
}: FeedProps) => {
  const [selectedOption, setSelectedOption] = useState<FeedFilterOption>("Mostrar")
  const [myPosts, setMyPosts] = useState(false)
  const [showFeedBar, setShowFeedBar] = useState(false)
  const [lastScrollPosition, setLastScrollPosition] = useState(0)
  const [dataLoaded, setDataLoaded] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)

  const isPhone = useIsPhone()
  const buttonsContainerRef = useRef<HTMLDivElement | null>(null)
  const observerRef = useRef<IntersectionObserver | null>(null)
  const loadMoreTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const feedView = useSelector(selectFeedView)
  const itemsFromOrder = useSelector(selectFeedItemsFromOrder)
  const items = useMemo(
    () => (dataLoaded ? itemsFromOrder : []),
    [dataLoaded, itemsFromOrder]
  )

  useEffect(() => {
    const options = {
      root: null,
      rootMargin: isPhone ? "-50px 0px 0px 0px" : "0px",
      threshold: [0, 0.9],
    }

    observerRef.current = new IntersectionObserver((entries) => {
      const [entry] = entries
      const currentScroll = window.scrollY
      const direction = currentScroll > lastScrollPosition ? "down" : "up"
      setLastScrollPosition(currentScroll)

      if (direction === "down") {
        setShowFeedBar(entry.intersectionRatio < 0.9)
      } else if (direction === "up") {
        setShowFeedBar(
          entry.intersectionRatio < 0.9 &&
            currentScroll > (buttonsContainerRef.current?.offsetHeight || 0)
        )
      }
    }, options)

    if (buttonsContainerRef.current) {
      observerRef.current.observe(buttonsContainerRef.current)
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
    }
  }, [lastScrollPosition, isPhone])

  const { guard } = useActionGuard()

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        if (!posteos || posteos.length === 0) {
          setFeedView({ selectedOption: "recientes", order: "DESC", type: "DATE" })
          await fetchPosteosWithComments("DESC", "DATE")
        }
        if ((!allPortfolios || allPortfolios.length === 0) && userData?.id) {
          await refetchPortfolios()
        }
        resetRecentPost()
        setDataLoaded(true)
      } catch (error) {
        console.error("Error loading initial data:", error)
        setDataLoaded(true)
      }
    }

    if (userData?.id && !dataLoaded) {
      loadInitialData()
    }
  }, [userData?.id, dataLoaded])

  useEffect(() => {
    if (!dataLoaded) return

    setMyPosts(false)

    const handleOptionChange = async () => {
      try {
        const posteosList = posteos ?? []
        const ownPosteosList = ownPosteos ?? []
        const portfoliosList = allPortfolios ?? []
        const uid = userData?.id
        switch (selectedOption) {
          case "recomendados":
            setFeedView({ selectedOption: "recomendados", order: "ASC", type: "MG" })
            await fetchPosteosWithComments("ASC", "MG")
            break
          case "recientes":
            setFeedView({ selectedOption: "recientes", order: "DESC", type: "DATE" })
            await fetchPosteosWithComments("DESC", "DATE")
            break
          case "propios":
            setFeedView({ selectedOption: "propios" })
            await fetchOwnFeedContent(userData.id)
            setMyPosts(true)
            break
          default:
            if (selectedOption === "Mostrar") {
              setFeedView({
                selectedOption: "Mostrar",
                order: "DESC",
                type: "DATE",
                itemOrder: computeItemOrder(
                  posteosList,
                  ownPosteosList,
                  portfoliosList,
                  "Mostrar",
                  uid
                ),
              })
            } else {
              setFeedView({
                selectedOption,
                itemOrder: computeItemOrder(
                  posteosList,
                  ownPosteosList,
                  portfoliosList,
                  selectedOption,
                  uid
                ),
              })
            }
            break
        }
      } catch (error) {
        console.error("Error changing option:", error)
      }
    }

    handleOptionChange()
  }, [selectedOption, dataLoaded, userData?.id])

  useEffect(() => {
    if (recentPost?.user?.id === userData?.id) {
      setFeedView({ selectedOption: "recientes", order: "DESC", type: "DATE" })
      fetchPosteosWithComments("DESC", "DATE")
      setSelectedOption("recientes")
    }
  }, [recentPost, userData?.id])

  useEffect(() => {
    return () => {
      const current = loadMoreTimeoutRef.current
      if (current) clearTimeout(current)
    }
  }, [])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showDropdown && !(event.target as Element).closest(`.${styles.dropdown}`)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [showDropdown])

  const handleOpenMainModalTxt = () => {
    openFeedTextModal({ text: true })
  }

  const handleOptionClick = (option: FeedFilterOption) => {
    setSelectedOption(option)
    setShowDropdown(false)
  }

  const handleShuffle = useCallback(() => {
    if (myPosts || isLoadingMore || !["Mostrar", "recientes", "recomendados"].includes(selectedOption)) return
    setIsLoadingMore(true)
    if (loadMoreTimeoutRef.current) {
      clearTimeout(loadMoreTimeoutRef.current)
    }
    loadMoreTimeoutRef.current = setTimeout(() => {
      dispatchShuffleFeedOrder()
      setIsLoadingMore(false)
    }, 1500)
  }, [myPosts, isLoadingMore, selectedOption, dispatchShuffleFeedOrder])

  const generateStableKey = useCallback(
    (item: FeedItem, index: number) => {
      const baseKey =
        item.itemType === "portfolio" ? `portfolio-${item.id}` : `post-${item.id}`
      return `${baseKey}-${selectedOption}-${index}`
    },
    [selectedOption]
  )

  return (
    <div className={styles.container} style={{ gridArea: "feed" }}>
      <div className={styles.header}>
        <h2 className={styles.header_title}>Productos y servicios</h2>
        <div className={styles.header_select}>
          <div className={styles.dropdown}>
            <span className={styles.header_line} />
            <SlidersHorizontal
              size={14}
              style={{ color: "#000", marginRight: "1rem", minWidth: "1.4rem" }}
            />
            <button
              className={styles.dropdown_button}
              onClick={() => setShowDropdown(!showDropdown)}
            >
              {selectedOption === "Mostrar"
                ? "Mostrar"
                : selectedOption === "recientes"
                  ? "Todos (Recientes)"
                  : selectedOption === "recomendados"
                    ? "Todos (Recomendados)"
                    : selectedOption === "productos"
                      ? "Solo Productos"
                      : selectedOption === "servicios"
                        ? "Solo Servicios"
                        : selectedOption === "posteos"
                          ? "Solo Posteos"
                          : selectedOption === "propios"
                            ? "Mis Publicaciones"
                            : "Mostrar"}
              <ChevronDown
                size={16}
                className={`${styles.chevron} ${showDropdown ? styles.chevronRotated : ""}`}
              />
            </button>

            {showDropdown && (
              <div className={styles.dropdown_content}>
                <button
                  className={`${styles.dropdown_content_option} ${selectedOption === "recientes" ? styles.active : ""}`}
                  onClick={() => handleOptionClick("recientes")}
                >
                  Todos (Recientes)
                </button>
                <button
                  className={`${styles.dropdown_content_option} ${selectedOption === "recomendados" ? styles.active : ""}`}
                  onClick={() => handleOptionClick("recomendados")}
                >
                  Todos (Recomendados)
                </button>
                <button
                  className={`${styles.dropdown_content_option} ${selectedOption === "productos" ? styles.active : ""}`}
                  onClick={() => handleOptionClick("productos")}
                >
                  Solo Productos
                </button>
                <button
                  className={`${styles.dropdown_content_option} ${selectedOption === "servicios" ? styles.active : ""}`}
                  onClick={() => handleOptionClick("servicios")}
                >
                  Solo Servicios
                </button>
                <button
                  className={`${styles.dropdown_content_option} ${selectedOption === "posteos" ? styles.active : ""}`}
                  onClick={() => handleOptionClick("posteos")}
                >
                  Solo Posteos
                </button>
                <button
                  className={`${styles.dropdown_content_option} ${selectedOption === "propios" ? styles.active : ""}`}
                  onClick={() => handleOptionClick("propios")}
                >
                  Mis Publicaciones
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={styles.feedItemContainer}>
        {(userData?.id && !dataLoaded) || (componentLoading && (items?.length ?? 0) > 0) ? (
          Array.from({ length: 10 }).map((_, index) => (
            <FeedCardSkeleton key={`skeleton-${index}`} />
          ))
        ) : (items?.length ?? 0) > 0 && !componentLoading ? (
          <>
            {items.map((item, index) => {
              const stableKey = generateStableKey(item, index)
              if (item.itemType === "portfolio") {
                return <PortfolioCard key={stableKey} item={item} />
              }
              return <FeedCard key={stableKey} item={item} myPosts={myPosts} />
            })}
            {["Mostrar", "recientes", "recomendados"].includes(selectedOption) &&
              !myPosts &&
              !isLoadingMore && (
                <div className={styles.feedItemContainer_shuffle}>
                  <button
                    type="button"
                    className={styles.feedItemContainer_shuffle_btn}
                    onClick={handleShuffle}
                  >
                    Mezclar
                  </button>
                </div>
              )}
          </>
        ) : dataLoaded && (items?.length ?? 0) === 0 ? (
          <div className={styles.feedItemContainer_empty}>
            <div className={styles.feedItemContainer_empty_icon}>
              <FileText size={34} />
            </div>
            <h2 className={styles.feedItemContainer_empty_title}>
              No tenés publicaciones propias
            </h2>
            <p className={styles.feedItemContainer_empty_text}>
              Comparte novedades, fotos y actualizaciones con la comunidad
            </p>
            <button
              className={styles.feedItemContainer_empty_button}
              onClick={guard("open_feed_modal", handleOpenMainModalTxt)}
            >
              Crear publicación
            </button>
          </div>
        ) : (
          Array.from({ length: 10 }).map((_, index) => (
            <FeedCardSkeleton key={`skeleton-fallback-${index}`} />
          ))
        )}
      </div>
    </div>
  )
}

const mapStateToProps = (state: {
  general: { userData: FeedProps["userData"]; componentLoading?: boolean }
  feed: {
    posteos: FeedPost[]
    ownPosteos: FeedPost[]
    recentPost: FeedPost | null
    allPortfolios: FeedPortfolio[]
  }
}) => ({
  userData: state.general.userData,
  posteos: state.feed.posteos,
  ownPosteos: state.feed.ownPosteos,
  recentPost: state.feed.recentPost,
  componentLoading: state.general.componentLoading,
  allPortfolios: state.feed.allPortfolios,
})

export default connect(mapStateToProps, {
  openFeedTextModal,
  closeMainModal,
  fetchPosteosWithComments,
  fetchOwnFeedContent,
  resetRecentPost,
  refetchPortfolios,
  setFeedView,
  shuffleFeedOrder,
})(Feed)
